import { z } from 'zod'
import { melodyNoteSchema } from './melody.js'

/**
 * Stored melody for a Section, produced by the Melody Writer.
 *
 * Reuses `melodyNoteSchema` from Seymour's F.2 melody-suggestions output so
 * a generated candidate can hydrate the writer's editing state without a
 * shape conversion. The Writer extends with per-section state: which layer
 * the user is editing at, whether they're in guided or free-play mode, and
 * (at Layer 4) phrase markers.
 *
 * See docs/superpowers/specs/2026-05-17-melody-writer-design.md.
 */

export const melodyLineSchema = z.object({
  type: z.literal('melody-line'),
  sectionId: z.string().describe('Parent section entity id'),
  notes: z.array(melodyNoteSchema).describe('Ordered melody notes'),
  layer: z
    .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
    .describe('Highest layer the user has unlocked / is editing at'),
  mode: z
    .enum(['guided', 'free-play'])
    .describe('Writer mode — guided gates affordances by layer, free-play exposes all'),
  phraseMarkers: z
    .array(
      z.object({
        beatOffset: z.number().describe('Beats from start of section'),
        role: z.enum(['antecedent-end', 'consequent-end', 'climax']),
      })
    )
    .default([])
    .describe('Layer-4 phrase boundary annotations'),
})

export type MelodyLine = z.infer<typeof melodyLineSchema>
